import React, { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { FormInstance } from 'antd/lib/form';
import { useParams } from 'react-router-dom';
import AuthContext from '@packages/contexts/auth/authContext';

import { Client } from '../../types/Client';
import { DEFAULT_FEATURE_FORM_DATA, FeaturesFormData, FormConfigInterface, initClientFormConfig } from './types';
import { FormModeType, FORM_MODE } from '../../types/Form';
import ClientController from '../../controllers/client.controller';
import { getAllFeatureConfigs } from './utils/getters';

export interface ClientFormContextInterface {
    clientId?: string | undefined;
    data: FeaturesFormData;
    mode: string;
    client?: Client | undefined;
    formConfig: FormConfigInterface;
    resetFormFlag: boolean;
    isLoadingData: boolean;
    isNoClientFound: boolean;
    isWebhookEnabled: boolean;
    isApiForwarderEnabled: boolean;
    resetForm: (form: FormInstance) => void;
    reload: () => void;
    setIsLoadingData: (val: boolean) => void;
    setIsNoClientFound: (val: boolean) => void;
    setIsWebhookEnabled: (val: boolean) => void;
    setIsApiForwarderEnabled: (val: boolean) => void;
    setData: (val: FeaturesFormData) => void;
}

export const ClientFormContext = createContext<ClientFormContextInterface | null>(null);

export const useClientFormContext = (): ClientFormContextInterface => {
    const ctx = useContext(ClientFormContext);

    if (!ctx) {
        throw new Error('Missing context');
    }
    return ctx;
};

export const ClientFormContextProvider = ({ children }: { children: ReactNode }): JSX.Element => {
    const [resetFormFlag, setResetFormFlag] = useState<boolean>(false);
    const [isLoadingData, setIsLoadingData] = useState<boolean>(false);
    const [isNoClientFound, setIsNoClientFound] = useState<boolean>(false);
    const [shouldReload, setShouldReload] = useState<boolean>(false);
    const [client, setClient] = useState<Client | undefined>(undefined);
    const { clientId, mode }: { clientId: string | undefined; mode: string } = useParams();
    const [formConfig, setformConfig] = useState<FormConfigInterface>(initClientFormConfig(mode));
    const [data, setData] = useState<FeaturesFormData>(DEFAULT_FEATURE_FORM_DATA);
    const [isWebhookEnabled, setIsWebhookEnabled] = useState<boolean>(false);
    const [isApiForwarderEnabled, setIsApiForwarderEnabled] = useState<boolean>(false);
    const { user } = useContext(AuthContext);

    const getData = async (cId: string | number): Promise<void> => {
        try {
            setIsLoadingData(true);
            const c = await ClientController.getClientById(Number(cId));
            setClient(c);
            setIsNoClientFound(false);
        } catch (error) {
            setIsNoClientFound(true);
        } finally {
            setIsLoadingData(false);
        }
        try {
            setIsLoadingData(true);
            const retrieved = await getAllFeatureConfigs(cId, user);
            setIsWebhookEnabled(retrieved.webhook?.config?.enabled || false);
            setIsApiForwarderEnabled(retrieved.apiForwarder?.config?.enabled || false);
            setData(retrieved);
        } finally {
            setIsLoadingData(false);
        }
    };

    const resetForm = (form: FormInstance): void => {
        form.resetFields();
        setResetFormFlag(!resetFormFlag);
    };

    useEffect(() => {
        setformConfig({
            ...formConfig,
            formMode: (mode as FormModeType) || FORM_MODE.ADD,
            isAddMode: mode === FORM_MODE.ADD,
            isEditMode: mode === FORM_MODE.EDIT,
            isDetailMode: mode === FORM_MODE.DETAIL,
        });
        setData(DEFAULT_FEATURE_FORM_DATA);
        if (clientId && !formConfig.isAddMode) {
            getData(clientId);
        }
    }, [mode, clientId, shouldReload]);

    const reload = useCallback(() => {
        setShouldReload(!shouldReload);
    }, [shouldReload]);

    // eslint-disable-next-line react/jsx-no-constructed-context-values
    const values = {
        /* Data */
        data,
        client,
        clientId,
        formConfig,
        mode,
        resetFormFlag,

        /* State */
        isLoadingData,
        isNoClientFound,
        isWebhookEnabled,
        isApiForwarderEnabled,

        /* Callbacks */
        resetForm,
        reload,

        /* Setters */
        setData,
        setIsLoadingData,
        setIsNoClientFound,
        setIsWebhookEnabled,
        setIsApiForwarderEnabled,
    };
    return <ClientFormContext.Provider value={values}>{children}</ClientFormContext.Provider>;
};
